/**
 * The ONE place a screen decides which assessment session it is showing.
 *
 * WHY THIS EXISTS: several screens fell back to a session id typed into the source
 * ('1757192726463', '1755614136792', '1757447332377', '1', 43) when the URL and storage held
 * none. None of those sessions belongs to a customer — the first does not exist on prod at all.
 * Before the 2026-09-16 route auth sweep those routes answered anyway, so the screen rendered
 * an analysis of a session that was not the viewer's. After the sweep, ownership refuses them
 * with 404, and Project Dora's coordinator saw "Analysis Error — HTTP error! status: 404" on
 * Outside Pressures (2026-09-17).
 *
 * Resolution order:
 *   1. URL `?sessionId=` / `?assessmentId=`, or an `/assessment/:id/...` path.
 *   2. The session the viewer last opened — sessionStorage first (demo viewers), then
 *      localStorage, `activeSessionId` before `currentSessionId` (UnifiedNavigation's order).
 *   3. `GET /api/assessments/latest` — the caller's own newest session, scoped server-side.
 *   4. null. A caller renders "no assessment yet"; it NEVER substitutes an id.
 */
import apiConfig from '../config/apiConfig';
import { authHeaders } from './authHeaders';

const STORAGE_KEYS = ['activeSessionId', 'currentSessionId'] as const;

function usable(value: string | null | undefined): string | null {
  return value && value.trim() && value !== 'undefined' && value !== 'null' ? value.trim() : null;
}

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function getStoredSessionId(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search || '');
  const fromQuery = usable(params.get('sessionId')) || usable(params.get('assessmentId'));
  if (fromQuery) return fromQuery;

  const pathParts = (window.location.pathname || '').split('/');
  if (pathParts[1] === 'assessment' && usable(pathParts[2])) return pathParts[2];

  const session = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
  const local = typeof localStorage !== 'undefined' ? localStorage : undefined;
  for (const storage of [session, local]) {
    for (const key of STORAGE_KEYS) {
      const id = usable(safeGet(storage, key));
      if (id) return id;
    }
  }
  return null;
}

export async function resolveCurrentSessionId(): Promise<string | null> {
  const stored = getStoredSessionId();
  if (stored) return stored;

  try {
    const response = await fetch(`${apiConfig.getApiBaseUrl()}/assessments/latest`, { headers: authHeaders() });
    if (!response.ok) return null;
    const body = await response.json();
    const id = usable(body?.assessmentId != null ? String(body.assessmentId) : null);
    if (id) {
      try {
        localStorage.setItem('currentSessionId', id);
      } catch {
        // storage unavailable — the id is still returned for this render
      }
    }
    return id;
  } catch {
    return null;
  }
}
